import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  IterableDiffers,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Carrier } from '../../../user-dashboard/shared/carrier.model';
import { Trip } from '../../shared/trip.model';
import { Markers } from '../../../shared/markers';
import { TripPingLight } from '../../shared/trip-ping-light.model';
import { LoadHandover } from '../../../load-handover/shared/load-handover.model';
import { DistanceUnit } from '../../../shared/distance-unit';
import { ClusterIconStyle } from '@google/markerclustererplus';
import { TripStatus } from '../../shared/trip-status';
import { MapManagementService } from '../../shared/map-management-service';
import LatLngLiteral = google.maps.LatLngLiteral;
import MapTypeStyle = google.maps.MapTypeStyle;
import Map = google.maps.Map;
import * as moment from 'moment/moment';
import { DateTimezoneService } from '../../shared/date-timezone.service';
import { DateService } from '../../../shared/date.service';

@Component({
  selector: 'app-load-tracking-map',
  templateUrl: './load-tracking-map.component.html',
  styleUrls: ['./load-tracking-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadTrackingMapComponent implements OnInit, OnChanges, DoCheck {

  @Input()
  carrier: Carrier;

  @Input()
  selectedTrip: Trip;

  @Input()
  bucketTrips: Trip[];

  @Input()
  singleTrip = false;

  @Input()
  showAchievedPath: boolean;

  @Input()
  showForecastPath: boolean;

  @Input()
  canShowDebug = true;

  @Input()
  distanceUnit: DistanceUnit = DistanceUnit.KILOMETERS;

  @Input()
  showBucket: boolean;

  @Input()
  forecastPathReady = false;

  @Output()
  showBucketChange = new EventEmitter<boolean>();

  @Output()
  switcher = new EventEmitter();

  @Output()
  selectedTripChange = new EventEmitter<Trip>();

  @Output()
  showAchievedPathChange = new EventEmitter<boolean>();

  @Output()
  showForecastPathChange = new EventEmitter<boolean>();

  @Output()
  refreshEmitter = new EventEmitter();

  @Output()
  centerChangedEmitter = new EventEmitter<LatLngLiteral>();

  @Output()
  zoomEmitter = new EventEmitter<number>();

  @Output()
  goToEmitter = new EventEmitter<LatLngLiteral>();

  @Output()
  distanceUnitChange = new EventEmitter<DistanceUnit>();

  @Output()
  mapReady = new EventEmitter<Map>();

  @Output()
  centerViewEmitter = new EventEmitter();

  selectedLoadHandover: LoadHandover = null;
  TripStatus = TripStatus;
  truckCursorPosition: TripPingLight = null;

  excludeIsolatedPings         = true;
  hidePingsAfterDestinationHit = true;
  keepPingsWithHighAccuracy    = false;
  displayDatesInTripTimezone    = false;
  debug                        = false;

  lat  = this.mapManagementService.defaultLat;
  lng  = this.mapManagementService.defaultLng;
  zoom = this.mapManagementService.defaultZoom;

  passiveColors = [
    '#0078bf',
    '#48dbfb',
    '#1dd1a1',
    '#00d2d3',
    '#54a0ff',
    '#5f27cd',
    '#ff9ff3',
    '#feca57',
    '#ff6b6b'
  ];

  mapStyles: Array<MapTypeStyle> = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    }
  ];

  clusterStyle: Array<ClusterIconStyle> = [{
    url: Markers.CLASSIC,
    height: 51.2,
    width: 51.2,
    className: 'cluster-marker',
    anchorIcon: [51.2, 25.6],
    anchorText: [14, 0.5],
    textColor: '#000',
    textSize: 10
  }];

  passiveTrailerIconUrl = Markers.TRAILER.grey;
  activeTrailerIconUrl  = Markers.TRAILER.blue;

  chepDepartureIconUrl = Markers.DEPARTURE_LOAD.chep;
  customerDepartureIconUrl = Markers.DEPARTURE_LOAD.customer;
  chepArrivalIconUrl = Markers.ARRIVAL_LOAD.chep;
  customerArrivalIconUrl = Markers.ARRIVAL_LOAD.customer;

  handoverReleaseIconUrl     = Markers.RELEASE.blue;
  handoverTakeIconUrl        = Markers.PICK_UP.blue;
  handoverReleaseTakeIconUrl = Markers.RELEASE_AND_PICK_UP.blue;

  DistanceUnit = DistanceUnit;

  lastPingDate: string;
  lastPingDuration: string;
  lastPingFormat: boolean;

  showDetail = true;
  refreshing = false;

  differ: any;

  constructor(
    private cdr: ChangeDetectorRef,
    private differs: IterableDiffers,
    private mapManagementService: MapManagementService,
    public dateTimeZoneService: DateTimezoneService,
    private dateService: DateService,
  ) {}

  ngOnInit(): void {
    if (this.singleTrip) { // Customer View display
      this.displayDatesInTripTimezone = true;
    } else {
      this.dateService.localeChanged.subscribe(() => this.forceRefreshTripDetails());
      this.differ = this.differs.find(this.bucketTrips).create(null);
    }
    this.dateTimeZoneService.timezoneSourceChange(this.displayDatesInTripTimezone, this.selectedTrip?.timezone);
  }

  ngDoCheck(): void {
    if (!this.singleTrip) {
      const bucketChanges = this.differ.diff(this.bucketTrips);
      if (bucketChanges) {
        this.ngOnChanges({bucketTrips: {previousValue: null, currentValue: null, firstChange: null, isFirstChange: null}});
      }
    }
    if (this.selectedTrip?.timelineParamsInitialized) {
      this.updateLastPingInfos();
      this.ngOnChanges({selectedTrip: {previousValue: null, currentValue: null, firstChange: null, isFirstChange: null}});
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.cdr.detectChanges();
    if (changes.selectedTrip && !!changes.selectedTrip.currentValue && !changes.selectedTrip.currentValue.timezone) {
      this.displayDatesInTripTimezone = false;
      this.displayDatesInTripTimezoneChange();
    }
  }

  forceRefreshTripDetails(): void {
    this.refreshEmitter.emit();
  }

  updateLatLngLiteral(loadHandover: LoadHandover): void {
    this.selectedLoadHandover = loadHandover;
  }

  goToMarker(latLng: LatLngLiteral): void {
    this.goToEmitter.emit(latLng);
  }

  updateTruckCursor(ping: TripPingLight): void {
    this.truckCursorPosition = ping;
    this.cdr.detectChanges();
  }

  selectTrip(trip: Trip): void {
    this.selectedTripChange.emit(trip);
  }

  switchDetails(): void {
    const map = document.getElementsByClassName('map-container').item(0);
    const button = document.getElementsByClassName('trips-map-close-details').item(0);
    if (button.classList.contains('not-shown')) {
      map.classList.remove('without-details');
      button.classList.remove('not-shown');
    } else {
      map.classList.add('without-details');
      button.classList.add('not-shown');
    }
    this.showDetail = !this.showDetail;
  }

  onMapReady(map: Map): void {
    this.mapReady.emit(map);
  }

  centerView(): void {
    this.centerViewEmitter.emit();
  }

  switchMode(): void {
    this.switcher.emit();
  }

  private updateLastPingInfos(): void {
    if (this.selectedTrip && this.selectedTrip.latestPing) {
      const duration = moment.duration(Math.abs(this.selectedTrip.latestPing.createdAt.diff(moment())));
      this.lastPingDate = this.selectedTrip.latestPing.createdAt.format('D MMM LT')
      this.lastPingDuration = this.selectedTrip.latestPing.createdAt.fromNow();
      this.lastPingFormat = duration.asDays() > 7;
    }
  }

  public displayDatesInTripTimezoneChange() {
    this.dateTimeZoneService.timezoneSourceChange(this.displayDatesInTripTimezone, this.selectedTrip.timezone);
    this.refreshEmitter.emit();
  }
}
